/* eslint-disable no-alert, no-empty, one-var */
// TODO(kevinb): rewrite this using React
import {t} from "@lingui/core/macro";
import _ from "underscore";

import type {GqlFetchFn} from "@khan/data-gql";
import {Errors, KAError} from "@khan/logging";
import {DeployInfo, PermissionsInfo, RequestInfo} from "@khan/shared-core";
import {toAbsoluteUrl} from "@khan/urls";
import {LocalStore} from "@khan/util-local-store";

import type {
    UpdateProjectProgressData,
    UpdateProjectProgressVariables,
    UpdateTalkthroughProgressData,
    UpdateTalkthroughProgressVariables,
} from "#content-library/queries.ts";
import {mapToUserProgram} from "#content-library/types/gql-type-mappers.ts";
import $ from "#jquery/jquery-with-scratchpad-plugins.js";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import deprecatedReactRender from "#rendering/deprecated-react-render.tsx";
import {LegacyRequireIfLoaded} from "#scratchpad-base-package/legacy-require-if-loaded.ts";
import {
    Scratchpad,
    ScratchpadRevision,
    UserScratchpad,
} from "#scratchpad-base-package/scratchpads.ts";
import {ScratchpadChallenge} from "#scratchpad-challenge-package/scratchpad-challenge.ts";
import APIActionResults from "#shared-package/api-action-results.ts";
import ProfileModel from "#shared-package/profile-model.ts";

import {createProgram} from "./queries/create-program.ts";
import {deleteProgramByID} from "./queries/delete-program.ts";
import {
    mapToGqlContentType,
    mapToGqlSpinoffOriginKind,
} from "./queries/gql-type-mappers.ts";
import {hideProgramEverywhere} from "./queries/hide-program-everywhere.ts";
import {hideProgramFromHotlist} from "./queries/hide-program-from-hotlist.ts";
import {spinOffProgram} from "./queries/spin-off-program.ts";
import {updateProgramSettings} from "./queries/update-program-settings.ts";
import {updateProgram} from "./queries/update-program.ts";
import ScratchpadView from "./scratchpad-view.tsx";
import {documentationTitles} from "./strings/documentation-titles.ts";

const MS = 1000;
const LOGGING_INTERVAL = 10 * MS; // ms

export type SaveProgramFn = (
    gqlFetch: GqlFetchFn,
    isSpinoff: boolean,
) => Promise<void>;

export type UserAuthoredContentType = "pjs" | "sql" | "webpage";

export type Program = {
    id: string;
    title: string | null | undefined;
    relativeUrl: string | null | undefined;
    revision: {
        code: string | null | undefined;
    };
    userAuthoredContentType: UserAuthoredContentType;
};

type ProgramCategory =
    | "CHALLENGE"
    | "DOCUMENTATION"
    | "OFFICIAL"
    | "PROJECT"
    | "SANCTIONED"
    | "TUTORIAL";

export type ProgramSettings = {
    height: number;
    width: number;
    category: ProgramCategory | null | undefined;
    description: string | null | undefined;
};

export const ScratchpadUI: any = {
    dom: {
        PAGE: ".scratchpad-page",
        EDITOR: ".scratchpad-editor",
        CANVAS_LOADING: ".scratchpad-canvas-loading",
        OUTPUT_FRAME: "#output-frame",
        ALL_OUTPUT: "#output, #output-frame",
        DRAG_HANDLE: ".scratchpad-drag-handle",
        SCRATCHPAD_WRAP: ".scratchpad-wrap",
        SCRATCHPAD_WRAP_OUTER: ".scratchpad-wrap-outer",
        CANVAS_WRAP: ".scratchpad-canvas-wrap",
        EDITOR_WRAP: ".scratchpad-editor-wrap",
    },

    // These content types actually have no output, so we hide
    // their output frame
    noOutput: {
        document: true,
    },

    /**
     * Redirect to the specified url, skipping the unsaved work check
     */
    forceRedirect: function (url: string) {
        ScratchpadUI.triggerRemove();

        // Skip unsaved work check
        $(window).unbind("beforeunload.scratchpad");

        const urlState = {url: url} as const;
        $(ScratchpadUI).trigger("redirect", urlState);

        // Load the URL in the top frame, to handle embedded scratchpad cases
        // NOTE(matthewcurtis): this breaks Cypress and needs to be reconsidered
        // see: https://khanacademy.atlassian.net/browse/LP-12231
        // @ts-expect-error - TS2531 - Object is possibly 'null'.
        top.location.href = urlState.url;
    },

    /**
     * Initialize custom keybindings and overrides used on the scratchpad.
     */
    initKeybindings: function () {
        $(document).off("keydown", scratchpadListenKeydown);
        $(document).on("keydown", scratchpadListenKeydown);
    },

    canEditCurrentScratchpad: function (): any {
        return (
            ScratchpadUI.scratchpad.isNew() ||
            ScratchpadUI.scratchpad.isOwner() ||
            PermissionsInfo.hasAnyPermissions
        );
    },

    inEditMode: function (): boolean {
        // If we're not in a tutorial we're always assumed to be in edit mode.
        return !ScratchpadUI.embedded;
    },

    hasEditor: function (): boolean {
        const hideEditor =
            ScratchpadUI.embedded && ScratchpadUI.scratchpad.get("canvasOnly");
        return !hideEditor;
    },

    // Only disable copy/paste from outside for the intro JS challenges,
    //  as those are the ones that students are most likely to try to cheat
    //  (since teachers actually assign those, especially for DonorsChoose).
    // Students sometimes have legitimate reasons to copy/paste in
    //  the advanced challenges.
    shouldDisableChallengePaste: function (): boolean {
        return (
            ScratchpadUI.scratchpad.get("defaultUrlPath") &&
            ScratchpadUI.scratchpad
                .get("defaultUrlPath")
                .indexOf("/programming/") > -1
        );
    },

    handleStartOver() {
        // We have two separate cases in which we want the user to be able to
        // "start over". The first is when a phantom user goes to a new
        // scratchpad page; since we don't allow phantom users to save
        // scratchpads, we don't show a save button and instead show a "start
        // over" button that acts as a clear button so that the toolbar isn't
        // empty
        if (ScratchpadUI.scratchpad.isNew() && !PermissionsInfo.isLoggedIn) {
            ScratchpadUI.liveEditor.editor.text("");
            // The second case is when a user starts over a challenge
        } else {
            const baseScratchpad = ScratchpadUI.scratchpad;
            const code = baseScratchpad.getCode();
            ScratchpadUI.liveEditor.editor.text(code);
            $(ScratchpadUI).trigger("startCodeOver");
        }
    },

    handleUndo() {
        ScratchpadUI.liveEditor.editor.undo();
        ScratchpadUI.liveEditor.editor.focus();
    },

    handleToggleProgramProp(
        gqlFetch: GqlFetchFn,
        property: "deleted" | "hideFromHotlist",
    ): Promise<undefined> {
        const scratchpad = ScratchpadUI.scratchpad;

        const mutationFunction =
            property === "deleted"
                ? hideProgramEverywhere
                : hideProgramFromHotlist;

        const newVal = !scratchpad.get(property);

        // @ts-expect-error - TS2322 - Type 'Promise<void | undefined>' is not assignable to type 'Promise<undefined>'.
        return mutationFunction(gqlFetch, scratchpad.id, newVal).then(() => {
            // When soft deleting, we also hide from hotlist, but not the other
            // way around
            // sync-start:hide-program-everywhere 59546260 https://github.com/Khan/webapp/blob/master/services/programs/models/program.go
            if (property === "deleted" && newVal === true) {
                scratchpad.set({
                    deleted: newVal,
                    hideFromHotlist: newVal,
                });
            } else {
                scratchpad.set({
                    [property.toString()]: newVal,
                });
            }
            // sync-end:hide-program-everywhere
        });
    },

    handleDeleteProgram(gqlFetch: GqlFetchFn): Promise<undefined> {
        // @ts-expect-error - TS2322 - Type 'Promise<void | undefined>' is not assignable to type 'Promise<undefined>'.
        return deleteProgramByID(gqlFetch, ScratchpadUI.scratchpad.id).then(
            () => {
                ScratchpadUI.scratchpad.stopListening();
                ScratchpadUI.forceRedirect(ScratchpadUI.topic.relativeUrl);
            },
        );
    },

    setTitle(title: string): Promise<void> {
        return new Promise<void>((resolve, error) => {
            const scratchpad = ScratchpadUI.scratchpad;
            scratchpad.setTitle(title);
            if (scratchpad.get("userAuthoredContentType") === "webpage") {
                ScratchpadUI.updateCodeFromScratchpadTitle();
            }
            resolve();
        });
    },

    captureScreenshot(): Promise<any> {
        return new Promise((resolve) => {
            ScratchpadUI.liveEditor.getScreenshot((screenshot) => {
                resolve(screenshot);
            });
        });
    },

    /**
     * This is a new implementation for saving program settings that uses
     * GraphQL and separates out the logic of updating program settings
     * from saving a full program (revision).
     */
    async _saveProgramSettings(gqlFetch: GqlFetchFn): Promise<undefined> {
        const scratchpad = ScratchpadUI.scratchpad;
        try {
            const programId = scratchpad.id;
            let savedProgramSettings: ProgramSettings | null | undefined = null;
            savedProgramSettings = await updateProgramSettings(gqlFetch, {
                programId,
                category: scratchpad.get("category")?.toUpperCase() || null,
                description: scratchpad.get("description"),
                height: scratchpad.get("height"),
                width: scratchpad.get("width"),
            });

            // Note that for user-created programs, the `translatedDescription`
            // is the same value as the program's `description` field. The
            // frontend uses `translatedDescription` when displaying the
            // program's info, and `description` when editing the program
            // fields in the settings modal.
            scratchpad.set(
                "translatedDescription",
                savedProgramSettings.description,
            );

            $(ScratchpadUI).trigger("saveCompleted", scratchpad);
        } catch (error: any) {
            $(ScratchpadUI).trigger("saveFailed");
            throw t`Error saving your creation to the server. Please try again!`;
        }
    },
    /**
     * Save, Create (new), or SpinOff a Program via GraphQL.
     */
    async saveProgram(
        gqlFetch: GqlFetchFn,
        isSpinoff: boolean,
        saveSettings: boolean,
    ): Promise<undefined> {
        // Saving settings is independent of the program revision. If
        // we only modified the program settings, we handle the update
        // separately and return early.
        if (saveSettings) {
            return ScratchpadUI._saveProgramSettings(gqlFetch);
        }
        let scratchpad = ScratchpadUI.scratchpad;
        const isNew = scratchpad.isNew();
        const screenshot = await ScratchpadUI.captureScreenshot();
        const title = scratchpad.get("title");
        const curationNodeSlug = ScratchpadUI.topicSlug;
        const revisionData = {
            code: ScratchpadUI.liveEditor.editor.text(),
            folds: ScratchpadUI.liveEditor.editor.getAllFolds(),
            imageUrl: screenshot,
            configVersion: scratchpad.getVersion(),
        } as const;

        const originProgramId = scratchpad.id;
        const originRevisionId = scratchpad.currentRevision().id;
        // The backend will parse an int for originId if
        // contentKind is "Scratchpad", otherwise it accepts a string
        const originProgramKind = scratchpad.get("contentKind");
        if (isSpinoff) {
            scratchpad = scratchpad.fork();
        }

        const revision = scratchpad.currentRevision();
        $(ScratchpadUI).trigger("saveData", revisionData);
        revision.set(revisionData);

        try {
            let savedProgram: Program | null | undefined = null;
            if (isSpinoff) {
                const originKind = mapToGqlSpinoffOriginKind(originProgramKind);
                savedProgram = await spinOffProgram(gqlFetch, {
                    // originProgramId will be a number if the origin is a
                    // Program, it'll be a string if we are spinning off from
                    // content. The GraphQL API requires a string.
                    originProgramOrContentId: originProgramId.toString(),
                    originProgramKind: originKind,
                    title,
                    revision: revisionData,
                    curationNodeSlug,
                    ...(originRevisionId ? {originRevisionId} : null),
                });
            } else if (isNew) {
                const contentType = mapToGqlContentType(
                    scratchpad.get("userAuthoredContentType"),
                );
                savedProgram = await createProgram(gqlFetch, {
                    title,
                    userAuthoredContentType: contentType,
                    revision: revisionData,
                    curationNodeSlug,
                });
            } else {
                const programId = scratchpad.id;
                savedProgram = await updateProgram(gqlFetch, {
                    programId,
                    title,
                    revision: revisionData,
                });
            }

            // If it was successful:
            // If a new scratchpad was just saved, clear the
            // localStorage cache
            LocalStore.del(ScratchpadUI.localStorage.SCRATCHPADS);

            // The server may modify fields upon save, and we want
            // to re-render if anything has changed (for example,
            // description becomes the translatedDescription in
            // many cases)
            scratchpad.set(scratchpad.parse(savedProgram));

            // Set the relativeURL for redirection
            scratchpad.set("relativeUrl", savedProgram.relativeUrl);

            // For projects: Clear the stashed code.
            // For challenges: always show the stashed code because it
            // would look like a bug that the challenge is completed but
            // there is no user code.
            if (isSpinoff && ScratchpadUI.scratchpad.isProject()) {
                ScratchpadUI.stashUserCode(null, "");
            }

            // Redirect the user if this is a fork or new project
            if (isSpinoff || isNew) {
                ScratchpadUI.forceRedirect(scratchpad.pathForShow());
            }

            $(ScratchpadUI).trigger("saveCompleted", scratchpad);
        } catch (error: any) {
            $(ScratchpadUI).trigger("saveFailed");
            if (error.message.includes("Request Entity Too Large")) {
                throw t`Your creation is too large. Please shorten the content and try again.`;
            }
            throw t`Error saving your creation to the server. Please try again!`;
        }
    },

    registerCodeRun(callback: () => unknown) {
        $(ScratchpadUI).on("runCode", callback);
    },

    registerCodeChange(callback: () => unknown) {
        $(ScratchpadUI).on("codeChange", _.debounce(callback, 300));
    },

    registerUserCodeChange(callback: () => unknown) {
        $(ScratchpadUI).on("userChangedCode", _.debounce(callback, 300));
    },

    registerPlayUpdate(callback: (arg1: number) => unknown) {
        ScratchpadUI.liveEditor.record.bind({
            playUpdate: function () {
                callback(ScratchpadUI.liveEditor.record.currentTime() / 1000);
            },
        });
    },

    onSeekStart() {
        ScratchpadUI.wasPlaying = ScratchpadUI.liveEditor.record.playing;
        ScratchpadUI.liveEditor.record.seeking = true;
        ScratchpadUI.liveEditor.record.pausePlayback();
    },

    onSeek(time: number) {
        ScratchpadUI.liveEditor.record.trigger("beforeSeek");
        ScratchpadUI.liveEditor.seekTo(time);
    },

    onSeekEnd() {
        ScratchpadUI.liveEditor.record.seeking = false;
        if (ScratchpadUI.wasPlaying) {
            ScratchpadUI.liveEditor.record.play();
        }
    },

    /**
     * Toggles the visibility of the spin-off button depending upon the
     * editor's environment.
     */
    isSpinOffAllowed(): boolean {
        // Hide the spin-off button when:
        // - We're on the challenge page. For challenge pages, we instead show
        //   a BIG save as spin-off when the challenge is complete.
        // - We're on a redirected project spin-off
        const scratchpad = ScratchpadUI.scratchpad;
        const isChallenge = scratchpad.isChallenge();
        const isUnfinishedChallenge =
            isChallenge && !ScratchpadUI.userScratchpad.get("isComplete");
        return !(scratchpad.isNew() || isUnfinishedChallenge);
    },

    hasUnsavedChanges: function (): boolean {
        const scratchpad = ScratchpadUI.scratchpad;
        const code = ScratchpadUI.liveEditor.editor.text();
        const isSaveable =
            ScratchpadUI.isSaveable() ||
            (!scratchpad.isTalkie() && ScratchpadUI.isSpinOffAllowed());
        let revisionCode = scratchpad.getCode();
        // We stash code for projects and challenges for user changes to the
        // scratchpad.  If we have stashed code, use that for the comparison.
        if (ScratchpadUI.stashedCode) {
            revisionCode = ScratchpadUI.stashedCode;
        }
        return isSaveable && $.trim(code) !== $.trim(revisionCode);
    },

    // The current scratchpad can be saved if either of the following are true:
    //  1. It's a new scratchpad and the current user is logged in
    //  2. It's a pre-existing scratchpad created by the current logged in user
    //  3. It's owned by KA and the user is a dev
    isSaveable: function (): boolean {
        return (
            PermissionsInfo.isLoggedIn &&
            ScratchpadUI.inEditMode() &&
            ScratchpadUI.canEditCurrentScratchpad()
        );
    },

    triggerSave: function (saveSettings: boolean) {
        if (ScratchpadUI._saveHandler) {
            ScratchpadUI._saveHandler(saveSettings);
        }
    },

    triggerSpinOff: function () {
        if (ScratchpadUI._spinOffHandler) {
            ScratchpadUI._spinOffHandler();
        }
    },

    // NOTE(jeresig): These are loading in dynamically once the user starts
    // editing the scratchpad (used for better hinting)
    loadDocumentationScratchpads: function () {
        // Make sure we don't load this more than once, and only do it
        // if there actually is a frame in the live editor
        if (!ScratchpadUI.liveEditor.hasFrame()) {
            return;
        }

        // Send the documentation for better error messages
        ScratchpadUI.liveEditor.postFrame({documentation: documentationTitles});
    },

    // We don't let admins save changes to the description/etc
    // unless they're working in English.  (This is because if
    // they're on a Spanish page, we'll display the description
    // in Spanish, so even if they don't change it, if they hit
    // save it will overwrite the English description with the
    // Spanish one, in the datastore.)  Obviously, this only
    // applies to scratchpads we translate into multiple
    // languages; otherwise the datastore stores in the native
    // language, which may or may not be English.
    displayLanguageAllowsEditing: function (): boolean {
        return (
            !!ScratchpadUI.scratchpad &&
            (!ScratchpadUI.scratchpad.get("category") ||
                RequestInfo.kaLocale === "en")
        );
    },

    getHostName: function (): string {
        // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
        // eslint-disable-next-line no-restricted-syntax
        return window.location.host.toLowerCase();
    },

    isProdServer: function (this: any): boolean {
        const host = this.getHostName();
        return (
            /khanacademy\.org/.test(host) ||
            /khan-academy\.appspot\.com/.test(host)
        );
    },

    prefixDomain: function (this: any, relativeUrl: string): string {
        if (this.isProdServer()) {
            const host = this.getHostName();
            // Filter appspot requests to the correct subdomain on
            // kasandbox.org
            const subdomain = /^(.*)-dot-khan-academy\.appspot\.com/.test(host)
                ? RegExp.$1
                : "www";
            return "https://" + subdomain + ".kasandbox.org" + relativeUrl;
        }

        // On a dev server, we don't want to proxy through kasandbox.org.
        return relativeUrl;
    },

    prefixStatic: function (relativeUrl: string): string {
        // eslint-disable-next-line @khan/frontend/dont-hardcode-cdn
        return `https://cdn.kastatic.org${relativeUrl}`;
    },

    sandboxUrl: function (this: any): string {
        const contentType = ScratchpadUI.scratchpad.get(
            "userAuthoredContentType",
        );
        let relativeUrl = `/computer-programming/exec/${contentType}?embedded=1`;

        // These content types actually have no output, so we hide
        // their output frame
        if (contentType in ScratchpadUI.noOutput) {
            return relativeUrl;
        }

        if (relativeUrl.indexOf("lang=") < 0) {
            // NOTE: This specific case works with kaLocale because all locales
            // are valid language tags.
            relativeUrl += "&lang=" + RequestInfo.kaLocale;
        }

        // In order to make sure requests get sent to the right module and
        // version, we need to add the host as the sub-domain.
        // kasandbox.org will then use this to proxy things to the correct GAE
        // host.
        relativeUrl = this.prefixDomain(relativeUrl);

        return relativeUrl;
    },

    /**
     * Determines the tab type to open for the specified feedback type.
     * Tabs actually get opened in Discussions
     * @param feedbackType The type of feedback to get the tab name for.
     * @return The tab name to open or "" if there is no corresponding tab.
     */
    getTabFromFeedbackType: function (feedbackType: string): string {
        if (feedbackType === "projecthelp") {
            return "projectfeedback";
        }
        if (feedbackType === "comment") {
            return "comments";
            // There are no tabs for Project Eval related feedback types.
            // The qa_expand_key param will be handled by the evaluation code.
            // Evaluations are shown at the top of a program in
            // projectfeedback-form.jsx.
        }
        if (
            feedbackType === "projecteval" ||
            feedbackType === "projectevalanswer"
        ) {
            return "";
        }
        return "questions";
    },

    init: function (this: any, options: any): any {
        options = options || {};
        const {profile} = options;
        const userId = profile && profile.get("userId");
        // This is used by Scratchpad#isOwner in scratchpad.jsx to
        // determine if the current user is the scratchpad's owner.
        this.currentUserKaid = profile && profile.get("kaid");

        // Set the topic used for product annotations for
        // BigBingo conversions. Currently, can either be passed in the topic
        // in options (for tutorials), passed in from a saved scratchpads
        // (the forkedFromTopic option) or defaulted to computer-programming
        // for new projects outside of tutorials.
        ScratchpadUI.topicSlug =
            (options.scratchpad && options.scratchpad.forkedFromTopic) ||
            options.topic.slug ||
            "computer-programming";

        // Init local storage, note that changing this local storage name
        // could lead to some users losing code not yet saved in
        // /computer-programming/new
        let localStorageNewName = "cs-scratchpad-new" + userId;
        if (options.userAuthoredContentType) {
            localStorageNewName += "-" + options.userAuthoredContentType;
        }
        ScratchpadUI.localStorage = $.extend(ScratchpadUI.localStorage || {}, {
            // key used to persist new scratchpads in localStorage
            SCRATCHPADS: localStorageNewName,
        });

        const oldStylePathMatch = /\/(\d+)(\/embedded)?$/.exec(
            // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
            // eslint-disable-next-line no-restricted-syntax
            window.location.pathname,
        );
        options.id =
            options.id || (oldStylePathMatch && oldStylePathMatch[1]) || "";
        options.embedded =
            (oldStylePathMatch && oldStylePathMatch[2]) ||
            options.embedded ||
            ScratchpadUI.embedded;
        // We may also embed new-style "Scratchpad"-like published content.
        if (!options.id) {
            const newStylePathMatch = /([A-Za-z]+):(\w+)(\/embedded)?$/.exec(
                // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
                // eslint-disable-next-line no-restricted-syntax
                window.location.pathname,
            );
            if (newStylePathMatch) {
                options.contentKind = newStylePathMatch[1];
                options.id = newStylePathMatch[2];
                options.embedded = true;
            }
        }
        ScratchpadUI.embedded = options.embedded;
        ScratchpadUI.topic = options.topic;

        const queryParams = new URLSearchParams(
            // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
            // eslint-disable-next-line no-restricted-syntax
            window.location.search,
        );

        const el = options.el || ".scratchpad-page";
        ScratchpadUI.$el = el ? $(el) : $(document);
        ScratchpadUI.el = ScratchpadUI.$el[0];
        // We remove all previously attached event listeners,
        //  so that if initAndBind is called twice without page reload
        //  as in tutorial view, the event listeners aren't bound twice
        ScratchpadUI.$el.off();

        if (options.scratchpad) {
            // We pipe the bootstrapped data through the Scratchpad's parse so
            // that it acts exactly as if it had been fetched from the server
            ScratchpadUI.scratchpad = new Scratchpad();
            ScratchpadUI.scratchpad.set(
                ScratchpadUI.scratchpad.parse(options.scratchpad),
            );
        } else {
            const newTitle = ScratchpadUI.termMap["new"];
            // sync-start:new-scratchpad 605314878 ./apps/khanacademy/src/tutorial-scratchpad-package/new-user-program-page.tsx
            ScratchpadUI.scratchpad = new Scratchpad({
                title: newTitle,
                translatedTitle: newTitle,
                category: null,
                difficulty: null,
                userAuthoredContentType: options.userAuthoredContentType,
                revision: new ScratchpadRevision({
                    code: ScratchpadUI.termMap["code"] || "",
                    created: new Date(),
                }),
            });
            // sync-end:new-scratchpad

            // Load saved data from localStorage
            const results = LocalStore.get(
                ScratchpadUI.localStorage.SCRATCHPADS,
            );

            if (results) {
                const rehydratedScratchpad = ScratchpadUI.scratchpad.parse(
                    results.scratchpad,
                );

                // Occasionally, a bug will cause a "blank" scratchpad to be saved
                // to localStorage. We don't want to repopulate a blank scratchpad.
                const validRehydratedCode =
                    rehydratedScratchpad.revision &&
                    rehydratedScratchpad.revision.get("code");

                if (validRehydratedCode) {
                    ScratchpadUI.scratchpad.set(rehydratedScratchpad);
                }
            }
        }
        ScratchpadUI.userScratchpad = new UserScratchpad({
            scratchpadId: ScratchpadUI.scratchpad.get("id"),
            scratchpadKind: ScratchpadUI.scratchpad.get("contentKind"),
        });
        ScratchpadUI.originScratchpad = new Scratchpad();
        if (options.originScratchpad) {
            ScratchpadUI.originScratchpad.set(
                ScratchpadUI.originScratchpad.parse(options.originScratchpad),
            );
        }
        const scratchpad = ScratchpadUI.scratchpad;
        ScratchpadUI.trusted = options.embedded;
        ScratchpadUI.localStorage = $.extend(ScratchpadUI.localStorage || {}, {
            // key used to persist whether a scratchpad has been seen as completed
            SEEN_COMPLETED_PROJECT:
                "cs-scratchpad-seen-completed" +
                userId +
                "-" +
                scratchpad.get("id"),
        });

        const contentType = scratchpad.get("userAuthoredContentType");

        // Set up just enough of the challenges code to have a ScratchpadUI.challenge
        // that can be passed into the root ScratchpadView component.
        ScratchpadUI.challenge = null;
        if (scratchpad.isChallenge()) {
            ScratchpadUI.challenge = new ScratchpadChallenge({
                contentType: contentType,
                lessonId: ScratchpadUI.topic.contentId,
                updateChallengeProgress: options.updateChallengeProgress,
                userScratchpad: ScratchpadUI.userScratchpad,
                onComplete: options.onComplete,
                onStart: options.onStart,
            });
        }

        if (scratchpad.isProject()) {
            ScratchpadUI.updateProjectProgress = (
                stashedCode: UpdateProjectProgressVariables["stashedCode"],
                status: UpdateProjectProgressVariables["status"],
            ): Promise<UpdateProjectProgressData> => {
                if (!options.updateProjectProgress) {
                    return Promise.reject(
                        new KAError(
                            "no handler set for updating Project progress",
                            Errors.Internal,
                        ),
                    );
                }

                const input: UpdateProjectProgressVariables = {
                    programId: ScratchpadUI.scratchpad.get("id"),
                    lessonId: ScratchpadUI.topic.contentId,
                    stashedCode,
                    status,
                };
                return options.updateProjectProgress(input).then((response) => {
                    const data = response.data.updateProjectProgress;
                    if (data.error?.code) {
                        return new KAError(
                            data.error?.code,
                            Errors.GraphqlResponse,
                        );
                    }
                    // If an error is absent, we expect programProgress to
                    // always return. Throw an error if it doesn't.
                    if (!data.programProgress) {
                        return new KAError(
                            "programProgress returned null without error code",
                            Errors.GraphqlResponse,
                        );
                    }

                    if (data.programProgress.completionStatus === "COMPLETE") {
                        options.onComplete?.();
                    }

                    ScratchpadUI.userScratchpad.set(
                        mapToUserProgram(data.programProgress),
                    );

                    // Respond to any action results if there were any updates.
                    if (data.actionResults) {
                        APIActionResults.respondToGraphQLAction(
                            data.actionResults,
                        );
                    }
                    return data;
                });
            };
        }

        const htmlElem: HTMLElement = document.documentElement as any;
        // Options for embedding an editor
        if (queryParams.get("editor") === "no") {
            htmlElem.className += " no-editor";
        }

        if (queryParams.get("output") === "no") {
            htmlElem.className += " no-output";
        }

        if (queryParams.get("buttons") === "no") {
            htmlElem.className += " no-buttons";
        }

        if (queryParams.get("author") === "no") {
            htmlElem.className += " no-author";
        }

        if (queryParams.get("blank") === "yes") {
            ScratchpadUI.blank = true;
        }

        ScratchpadUI.initUserScratchpad(options);
        ScratchpadUI.startScratch(queryParams, options);
    },

    initLiveEditor(queryParams: URLSearchParams, options: any) {
        const scratchpad = ScratchpadUI.scratchpad;
        const revision = scratchpad.currentRevision();
        const contentType = scratchpad.get("userAuthoredContentType");

        let cursor;
        let autoFocus = true;
        let settings: Record<string, any> = {};

        // Prevent the editor from automatically receiving focus
        if (queryParams.get("autoFocus") === "no") {
            autoFocus = false;
        }

        // If the browser window is small then we disable focusing the editor right away
        // Generally this will be the case for small mobile browsers.
        if ($(window).width() <= 1024) {
            autoFocus = false;
        }

        // Initialize any settings based upon what the external page passes in
        const settingsParam = queryParams.get("settings");
        if (settingsParam) {
            try {
                settings = JSON.parse(settingsParam);
            } catch (err: any) {}
        }

        // Load saved data from localStorage
        const results = LocalStore.get(ScratchpadUI.localStorage.SCRATCHPADS);
        if (results) {
            cursor = results.cursor;
        }

        /*
         * Communicate results back to the parent page.
         * (Used when scratchpad is embedded.)
         */
        let frameSource, frameOrigin;
        // Get the parent frame source and origin
        // Make sure we don't throw errors if it freaks out
        try {
            frameSource = window.parent;
            frameOrigin =
                queryParams.get("origin") || frameSource.location.origin;
        } catch (e: any) {}
        const postParent = function (data: {testsPassed: any}) {
            // Send the data to the frame using postMessage
            if (frameSource && frameOrigin && frameSource !== window) {
                // @ts-expect-error - TS2339 - Property 'id' does not exist on type '{ testsPassed: any; }'.
                data.id = queryParams.get("id") || "";

                // Catch any cross-origin issues that may pop up
                try {
                    frameSource.postMessage(JSON.stringify(data), frameOrigin);
                } catch (e: any) {}
            }
        };

        // If there is no user specific code, then we should grab the code out
        // of the revision
        let code = "";
        if (!ScratchpadUI.blank && !queryParams.get("code")) {
            code = queryParams.get("code") || scratchpad.getCode() || "";
        }

        // Parse talk-through playback commands
        let playback = scratchpad.getPlayback();
        if (playback) {
            playback = JSON.parse(playback);
        }

        // We want webpage scratchpads to always take up 50% of the width
        // on load.  Even if the saved scratchpad has a different width.
        let overrideWidth;
        if (scratchpad.isResizableType()) {
            // scratchpad-wrap width isn't available yet, so default the size
            // of the scratchpad to half of the parent container.
            overrideWidth = ScratchpadUI.hasEditor() ? "50%" : "100%";
        }
        const width =
            parseFloat(queryParams.get("width") ?? "") ||
            overrideWidth ||
            scratchpad.get("width") ||
            400;
        const height =
            parseFloat(queryParams.get("height") ?? "") ||
            scratchpad.get("height") ||
            400;

        let showYoutubeTimer;
        if (scratchpad.isTalkie()) {
            // If it takes too long to load, we let them know about Youtube option
            // TODO(somewhatabstract): If possible, use wonder blocks timing.
            // eslint-disable-next-line no-restricted-syntax
            showYoutubeTimer = window.setTimeout(function () {
                if (!ScratchpadUI.liveEditor.audioReadyToPlay()) {
                    ScratchpadUI.$el
                        .find(".scratchpad-editor-youtube-link")
                        .show();
                }
            }, 15 * 1000);
        }

        const imagesDir = ScratchpadUI.prefixStatic(
            "/third_party/javascript-khansrc/live-editor/build/images/",
        );

        // By default we use the Ace editor
        const editorType = "ace";
        const editorHeight = height;

        // The mp3 URL has moved off of the (now non-existent) revision onto
        // the Talkthrough model itself.
        let mp3;
        if (scratchpad.get("contentKind") === "Talkthrough") {
            mp3 = scratchpad.get("translatedMp3Url");
        } else {
            mp3 = revision.getMp3Url();
        }

        let tests;
        if (scratchpad.get("contentKind") === "Challenge") {
            tests = scratchpad.get("translatedTests");
        }
        ScratchpadUI.liveEditor = new ScratchpadUI.LiveEditor({
            el: ScratchpadUI.$el,
            editorType: editorType + "_" + contentType,
            outputType: contentType,
            width: width,
            height: height,
            editorHeight: editorHeight,
            newErrorExperience: true,
            autoFocus: autoFocus,
            code: code,
            validation: tests,
            version: scratchpad.get("configVersion"),
            cursor: cursor,
            settings: settings,
            recordingMP3: mp3,
            recordingCommands: playback && playback.commands,
            recordingInit: playback && playback.init,
            workersDir: ScratchpadUI.prefixStatic(
                "/third_party/javascript-khansrc/live-editor/build/workers/",
            ),
            externalsDir: ScratchpadUI.prefixStatic(
                "/third_party/javascript-khansrc/live-editor/build/external/",
            ),
            soundsDir: ScratchpadUI.prefixStatic(
                "/third_party/javascript-khansrc/live-editor/sounds/",
            ),
            imagesDir: imagesDir,
            jshintFile: ScratchpadUI.prefixStatic(
                (RequestInfo.kaLocale !== "en"
                    ? "/genfiles/translations/" + RequestInfo.kaLocale
                    : "") +
                    "/third_party/javascript-khansrc/live-editor/" +
                    "build/external/jshint/jshint.js",
            ),
            redirectUrl: toAbsoluteUrl("/computer-programming/link_redirector"),
            execFile: ScratchpadUI.sandboxUrl(),
            enableLoopProtect: !scratchpad.get("canvasOnly"),
        });

        if (scratchpad.isChallenge()) {
            ScratchpadUI.challenge.setLiveEditor(ScratchpadUI.liveEditor);
        }

        // Add a class for styling of the editor
        ScratchpadUI.$el.addClass("editor-" + editorType);
        ScratchpadUI.liveEditor.editor.on("change", () =>
            $(ScratchpadUI).trigger("codeChange"),
        );

        // Note that this does *not* fire when users use number scrubber
        // or color picker, only when they actually type code.
        ScratchpadUI.liveEditor.on("userChangedCode", () => {
            $(ScratchpadUI).trigger("userChangedCode");
        });
        ScratchpadUI.liveEditor.on("readyToPlay", function () {
            $(ScratchpadUI).trigger("readyToPlay");
            // Clear Youtube timer and hide Youtube link
            // TODO(somewhatabstract): If possible, use wonder blocks timing.
            // eslint-disable-next-line no-restricted-syntax
            window.clearTimeout(showYoutubeTimer);
            ScratchpadUI.$el.find(".scratchpad-editor-youtube-link").hide();
        });
        ScratchpadUI.liveEditor.on("canvasSizeUpdated", function (size) {
            const width = size.width;
            const height = size.height;
            ScratchpadUI.$el.find(ScratchpadUI.dom.CANVAS_WRAP).width(width);
            ScratchpadUI.$el
                .find(ScratchpadUI.dom.DRAG_HANDLE)
                .find("div")
                .height(height);
            if (ScratchpadUI.embedded) {
                if (queryParams.get("editor") === "no") {
                    // If the scratchpad page is being embedded then we need
                    // to set the dimensions on the page, as well.
                    $("html").width(width).height(height);
                } else if (queryParams.get("editor") === "yes") {
                    // If the scratchpad is embedded and the editor is visible,
                    // we want to ensure the iframe content width is large
                    // enough to allow horizontal scrolling, if necessary.
                    $("html").width(820);
                }
            }
        });

        // Force an update of the dimensions on load
        ScratchpadUI.liveEditor.trigger("canvasSizeUpdated", {
            width: width,
            height: height,
        });
        ScratchpadUI.liveEditor.listenTo(
            scratchpad,
            "change:width change:height",
            function () {
                ScratchpadUI.liveEditor.updateCanvasSize(
                    scratchpad.get("width"),
                    scratchpad.get("height"),
                );
            },
        );
        ScratchpadUI.liveEditor.off("update").on("update", function (data) {
            // Notify parent if tests passed, from Program.runTest,
            //  like if this is embedded inside a Khan Academy exercise
            if (data.results && frameSource && data.pass !== undefined) {
                // @ts-expect-error - TS2554 - Expected 1 arguments, but got 2.
                postParent({testsPassed: data.pass}, "*");
            } else if (data.action === "link-click") {
                const message = t`The link you clicked was on a user-created webpage.  Are you sure you would like to visit the following website?
${data.url}`;
                if (confirm(message)) {
                    window.open(data.url);
                }
            } else if (data.action === "page-info") {
                // Update the title if we're on a non-tutorial scratchpad
                // Otherwise, tutorial scratchpads on intl sites get en titles!
                if (!ScratchpadUI.trusted) {
                    const newTitle = data.title || ScratchpadUI.termMap["new"];
                    ScratchpadUI.scratchpad.setTitle(newTitle);
                }
            } else if (data.action === "sql-table-click") {
                let code = ScratchpadUI.liveEditor.editor.text();
                code = $.trim(code) + "\nSELECT * FROM " + data.table + ";";
                ScratchpadUI.liveEditor.editor.text(code);
            }

            options.onCodeChange?.(data);

            $(ScratchpadUI).trigger("update", data);
        });
        ScratchpadUI.liveEditor.on("runCode", function (data) {
            $(ScratchpadUI).trigger("runCode", data);
        });

        /*
         * Bind events for tracking the time that the user has spent listening
         * to the talkie.
         */
        const record = ScratchpadUI.liveEditor.record;
        let lastReport = undefined;
        let playLogInterval: undefined | number = undefined;

        const cancelPlayLogging = function () {
            // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
            // eslint-disable-next-line no-restricted-syntax
            clearInterval(playLogInterval);
            playLogInterval = undefined;
            lastReport = undefined;
        };

        const startLogging = function () {
            cancelPlayLogging();

            lastReport = record.currentTime();
            // `null` lastReport means the learner is playing this from the
            // beginning, for the first (or nth) time.
            if (lastReport === null) {
                options.onStart?.();
            }
            // @ts-expect-error - TS2322 - Type 'Timer' is not assignable to type 'number'.
            // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
            // eslint-disable-next-line no-restricted-syntax
            playLogInterval = setInterval(
                () => logTalkthrough(record.currentTime()),
                LOGGING_INTERVAL,
            );
        };

        const logTalkthrough = (
            lastMsWatched: UpdateTalkthroughProgressVariables["lastMsWatched"],
            fullyWatched: UpdateTalkthroughProgressVariables["fullyWatched"] = false,
            updateProgress: UpdateTalkthroughProgressVariables["updateProgress"] = false,
        ): Promise<UpdateTalkthroughProgressData | null> => {
            if (!options.updateTalkthroughProgress) {
                return Promise.reject(
                    new KAError(
                        "no handler set for updating Talkthrough progress",
                        Errors.Internal,
                    ),
                );
            }

            if (lastReport === undefined) {
                return Promise.resolve(null);
            }

            const timeElapsed = lastMsWatched - lastReport;
            if (timeElapsed < 1) {
                return Promise.resolve(null);
            }

            const input: UpdateTalkthroughProgressVariables = {
                fullyWatched: fullyWatched,
                lastMsWatched: lastMsWatched,
                lessonId: ScratchpadUI.topic.contentId,
                msDuration: Math.floor(record.endTime() ?? 0),
                msWatched: timeElapsed,
                programId: ScratchpadUI.scratchpad.get("id"),
                updateProgress: updateProgress,
            };
            return options.updateTalkthroughProgress(input).then((response) => {
                const data = response.data.updateTalkthroughProgress;
                if (data.error?.code) {
                    return new KAError(
                        data.error?.code,
                        Errors.GraphqlResponse,
                    );
                }
                // If an error is absent, we expect programProgress to
                // always return. Throw an error if it doesn't.
                if (!data.programProgress) {
                    return new KAError(
                        "programProgress returned null without error code",
                        Errors.GraphqlResponse,
                    );
                }

                // By updating Backbone model with returned user scratchpad
                // properties we trigger a UI update to reflect latest state
                if (updateProgress) {
                    ScratchpadUI.userScratchpad.set(
                        mapToUserProgram(data.programProgress),
                    );

                    if (data.programProgress.completionStatus === "STARTED") {
                        options.onStart?.();
                    }

                    if (data.programProgress.completionStatus === "COMPLETE") {
                        options.onComplete?.();
                    }
                }

                // Respond to any action results if there were any updates.
                if (data.actionResults) {
                    APIActionResults.respondToGraphQLAction(data.actionResults);
                }
                return data;
            });
        };

        record.on({
            beforeSeek: function () {
                logTalkthrough(record.currentTime());
                cancelPlayLogging();
            },

            playStarted: function () {
                startLogging();
            },

            playPaused: function () {
                if (record.currentTime() === 0) {
                    // Beginning or end
                    return;
                }

                // This will also update scratchpad progress for user
                logTalkthrough(record.currentTime(), false, true);
                cancelPlayLogging();
            },

            playEnded: function () {
                // This will also update scratchpad progress for user,
                // as well as mark scratchpad as fully watched
                logTalkthrough(record.endTime(), true, true);
                cancelPlayLogging();
            },
        });
    },

    initUserScratchpad: function (options: any): boolean {
        if (options.userScratchpad) {
            ScratchpadUI.userScratchpad.set(
                ScratchpadUI.userScratchpad.parse(options.userScratchpad),
            );
            return true;
        }
        return false;
    },

    initResizing: function (this: any) {
        if (ScratchpadUI.scratchpad.isResizableType()) {
            const minCanvas = 400;
            const minEditor = 410;

            const $handle = $(this.dom.DRAG_HANDLE);
            const $outputFrame = $(this.dom.OUTPUT_FRAME);
            const $scratchpadWrap = $(this.dom.SCRATCHPAD_WRAP);

            $handle.on("mousedown", function (e) {
                const initialPX = e.pageX;
                const initialWidth = $outputFrame.width();
                const drag = _.throttle(function (e) {
                    let delta = e.pageX - initialPX;
                    // Because I care <3
                    if ($scratchpadWrap.css("direction") === "rtl") {
                        delta = -delta;
                    }
                    let width = initialWidth - delta;
                    const totalWidth = $scratchpadWrap.width();
                    if (width < minCanvas) {
                        width = minCanvas;
                    } else if (totalWidth - width < minEditor) {
                        width = totalWidth - minEditor;
                    }
                    ScratchpadUI.liveEditor.updateCanvasSize(width);
                    return false;
                }, 30);

                $handle.addClass("dragging");

                // Stop the iframe from stealing events during drag
                $outputFrame.css("pointer-events", "none");

                $(document).on("mousemove", drag);
                $(document).one("mouseup", function () {
                    $(document).off("mousemove", drag);
                    $handle.removeClass("dragging");

                    $outputFrame.css("pointer-events", "auto");
                });
            });

            $handle.show();
        }
    },

    triggerRemove: function () {
        // Note: This is the time at which state should be saved
        // as the user is potentially leaving.
        $(ScratchpadUI).trigger("remove");
    },

    /**
     * Used for webpage scratchpads, will update a <title> tag if one
     * exists with the scratchpad title.
     */
    updateCodeFromScratchpadTitle: function () {
        let newTitle = ScratchpadUI.scratchpad.get("translatedTitle");
        const code = ScratchpadUI.liveEditor.editor.text();
        const titleRegex = /(<title\b[^>]*>)[^<>]*(<\/title>)/i;
        const matches = code.match(titleRegex);
        if (!matches) {
            return;
        }
        // We set read only for the next 500 milliseconds in case
        //  the user puts their cursor inside the <title> right
        //  after the change - if they press space, they may end up
        //  deleting more than they expect
        ScratchpadUI.liveEditor.editor.setReadOnly(true);
        // TODO(somewhatabstract): If possible, use wonder blocks timing.
        // eslint-disable-next-line no-restricted-syntax
        window.setTimeout(function () {
            ScratchpadUI.liveEditor.editor.setReadOnly(false);
        }, 500);
        newTitle = "<title>" + newTitle + "</title>";
        ScratchpadUI.liveEditor.editor.editor.replace(newTitle, {
            needle: matches[0],
        });
    },
    termMap: {} as any,

    renderScratchpad(
        queryParams: URLSearchParams,
        options: any,
        callback: () => unknown,
    ) {
        const {profile} = options;
        const scratchpad = ScratchpadUI.scratchpad;
        const revision = scratchpad.currentRevision();
        const contentType = scratchpad.get("userAuthoredContentType");

        // init() will be called more than once when we do client-side routing.
        // Even if this isn't the first time we're init()'ed, let's pretend that
        // we don't have a ScratchpadUI.liveEditor so this promise won't resolve
        // until we have a reference to the _new_ liveEditor.
        ScratchpadUI.liveEditor = null;

        // A promise that will resolve when the LiveEditor has a record object
        const liveEditorPromise = new Promise(
            (resolve: unknown, reject: unknown) => {
                const check = () => {
                    if (
                        ScratchpadUI.liveEditor &&
                        ScratchpadUI.liveEditor.record
                    ) {
                        // @ts-expect-error - TS2571 - Object is of type 'unknown'.
                        resolve(ScratchpadUI.liveEditor);
                    } else {
                        // TODO(somewhatabstract): If possible, use wonder blocks timing.
                        // eslint-disable-next-line no-restricted-syntax
                        window.setTimeout(check, 10);
                    }
                };
                check();
            },
        );

        let sandboxProps = "allow-pointer-lock allow-same-origin allow-scripts";
        // We need allow-popups so that webpage links can be clicked
        // and allow-modals so that they can use alert/prompt
        if (contentType === "webpage") {
            sandboxProps += " allow-popups allow-modals allow-forms";
        }

        const inProgressChallenge =
            scratchpad.isChallenge() &&
            !ScratchpadUI.userScratchpad?.get("isComplete");

        // This is the context that will be passed to the root ScratchpadView component.
        const context = {
            scratchpad: scratchpad,
            userScratchpad: ScratchpadUI.userScratchpad,
            originScratchpad:
                options.originScratchpad &&
                new Scratchpad(options.originScratchpad),
            creatorProfile:
                options.creatorProfile &&
                new ProfileModel(options.creatorProfile),
            discussion: options.discussion,
            challenge: ScratchpadUI.challenge,
            userAuthoredContentType: options.userAuthoredContentType,

            liveEditorPromise: liveEditorPromise,

            // Global
            embedded: !!options.embedded, // ScratchpadView expects a boolean
            embeddedAndWithFrame:
                options.embedded &&
                // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
                // eslint-disable-next-line no-restricted-syntax
                /\/embedded$/.test(window.location.pathname),
            appVersion: DeployInfo.staticVersion,
            sandboxUrl: ScratchpadUI.sandboxUrl(),

            // User-centric
            inEditModeAndCanEdit:
                ScratchpadUI.inEditMode() &&
                ScratchpadUI.canEditCurrentScratchpad(),
            canEdit: ScratchpadUI.canEditCurrentScratchpad(),
            isDeveloper: PermissionsInfo.hasAnyPermissions,
            isOwner: scratchpad.isOwner(),
            isModerator: !!(profile && profile.get("isModerator")),
            profileRoot: profile && profile.get("profileRoot"),

            // Scratchpad-centric
            translatedDescription: scratchpad.get("translatedDescription"),
            translatedTitle: scratchpad.get("translatedTitle"),
            isNew: scratchpad.isNew(),
            isDocumentation: scratchpad.get("category") === "documentation",
            isOfficial: scratchpad.isOfficial(),
            isProject: scratchpad.isProject(),
            isChallenge: scratchpad.isChallenge(),
            isUneditableProjectFork: !!(
                scratchpad.get("originIsProject") &&
                !ScratchpadUI.canEditCurrentScratchpad()
            ),
            hasAudio: scratchpad.isTalkie(),
            lastUpdatedTime: revision.get("created").toISOString(),
            sandboxProps: sandboxProps,
            youtubeUrl: scratchpad.getYoutubeUrl(),
            hasUnsavedChanges: () => ScratchpadUI.hasUnsavedChanges(),
            shouldHideRestart: shouldHideRestart,

            // UI-centric
            showShareButton:
                !scratchpad.isNew() &&
                (!profile || !profile.get("isChildAccount")) &&
                scratchpad.isOwner(),
            showProjectEvalArea: !!(
                profile &&
                (scratchpad.get("originIsProject") || scratchpad.isProject())
            ),
            showProjectHelpButton: !!(
                profile &&
                !profile.get("isChildAccount") &&
                ((scratchpad.isOwner() &&
                    !scratchpad.isTalkie() &&
                    !scratchpad.isChallenge()) ||
                    scratchpad.isProject())
            ),
            showStartOverButton:
                scratchpad.isChallenge() ||
                scratchpad.isProject() ||
                (scratchpad.isNew() && !PermissionsInfo.isLoggedIn),
            hideSave: !ScratchpadUI.inEditMode() && !scratchpad.isChallenge(),
            showDescription: !!(
                scratchpad.get("translatedDescription") ||
                scratchpad.isProject() ||
                scratchpad.get("originIsProject")
            ),
            hideDocumentation: !ScratchpadUI.hasEditor(),
            hideSpinOffs:
                scratchpad.isNew() ||
                inProgressChallenge ||
                !ScratchpadUI.hasEditor(),
            hideDiscussion: scratchpad.isNew() || inProgressChallenge,
            hideEditor:
                !ScratchpadUI.hasEditor() || queryParams.get("editor") === "no",
            // There's no benefit to showing voting controls for official
            // scratchpads. It's more of an indicator of traffic than actually
            // how good it is. And it clutters the UI.
            hideVotes: scratchpad.inTutorial(),
            hideVoteControls: scratchpad.inTutorial(),
            hideEditTitle: scratchpad.inTutorial(),
            hideDelete: scratchpad.inTutorial(),
            isSaveable: ScratchpadUI.isSaveable(),
            isSpinOffAllowed: ScratchpadUI.isSpinOffAllowed,
            useContentHeader: !!options.embedded,

            // UI Events
            onToggleProgramProp: ScratchpadUI.handleToggleProgramProp,
            onStartOver: ScratchpadUI.handleStartOver,
            onTriggerSpinoff: ScratchpadUI.triggerSpinOff,
            onUndo: ScratchpadUI.handleUndo,
            onSave: ScratchpadUI.triggerSave,
            onSeekStart: ScratchpadUI.onSeekStart,
            onSeek: ScratchpadUI.onSeek,
            onSeekEnd: ScratchpadUI.onSeekEnd,
            setTitle: ScratchpadUI.setTitle,
            deleteProgram: ScratchpadUI.handleDeleteProgram,
            saveProgram: ScratchpadUI.saveProgram,
            registerCodeRun: ScratchpadUI.registerCodeRun,
            registerCodeChange: ScratchpadUI.registerCodeChange,
            registerUserCodeChange: ScratchpadUI.registerUserCodeChange,
            registerTriggerSave: (callback: (arg1: boolean) => void) =>
                (ScratchpadUI._saveHandler = callback),
            registerTriggerSpinOff: (callback: () => void) =>
                (ScratchpadUI._spinOffHandler = callback),
            registerPlayUpdate: ScratchpadUI.registerPlayUpdate,

            // Names based on user authored content type
            newTitle: ScratchpadUI.termMap["new"],
            restartLabel: ScratchpadUI.termMap["restart"],

            topic: options.topic,
            topicSlug: ScratchpadUI.topicSlug,
            isComputerProgramming:
                options.topic.slug === "computer-programming",

            private: !!scratchpad.get("byChild"),
            scratchpadKey: scratchpad.get("key"),
        } as const;

        if (!scratchpad.get("byChild") && scratchpad.get("key")) {
            const discussionMeta = {
                // Voting widget
                sumVotesIncremented: scratchpad.get("sumVotesIncremented"),
                voteUpOnly: true,
                upVoted: options.upVoted,
                scratchpadKey: scratchpad.get("key"),
                showModControls: !!(profile && profile.get("isModerator")),
                flags: scratchpad.get("flags"),
                flaggedBy: scratchpad.get("flaggedBy"),
                flaggedByUser: options.flaggedByUser,
                numberOfFlags: scratchpad.get("flags").length,
                flagButton: true,
                definitelyNotSpam: scratchpad.get("definitelyNotSpam"),
                // so our official scratchpads can't be flagged
                hideFlagControls:
                    scratchpad.inTutorial() || scratchpad.isCategorySet(),
                showAuthorControls: scratchpad.isOwner(),
                inQueue: false,
                // This is rare but if a moderator looks at their own program it
                // should not have a mod-controls delete button in addition to
                // the user controls delete button
                hideModDelete:
                    scratchpad.isOwner() || PermissionsInfo.hasAnyPermissions,
            } as const;
            // @ts-expect-error - TS2339 - Property 'discussionMeta' does not exist on type '{ readonly scratchpad: any; readonly userScratchpad: any; readonly originScratchpad: any; readonly creatorProfile: any; readonly discussion: any; readonly challenge: any; readonly userAuthoredContentType: any; ... 65 more ...; readonly scratchpadKey: any; }'.
            context.discussionMeta = discussionMeta;
        }

        // The following code makes sure that permalinked discussions
        //  open in the appropriate tab.
        if (!options.embeddedAndWithFrame) {
            // If discussion information was not passed in, we set the topic
            // slug for it, if at all possible.
            if (!options.discussion) {
                options.discussion = {} as Record<string, string>;
            }
            if (queryParams.get("qa_expand_type")) {
                options.discussion.expandFeedbackType =
                    ScratchpadUI.getTabFromFeedbackType(
                        queryParams.get("qa_expand_type"),
                    );
            }
            if (queryParams.get("qa_expand_key")) {
                options.discussion.qaExpandKey =
                    queryParams.get("qa_expand_key");
            }
        }

        // Render the scratchpad
        const ViewComponent = options.renderComponent ?? ScratchpadView;
        $(ScratchpadUI).trigger("render");
        deprecatedReactRender(
            <ViewComponent {...context} />,
            ScratchpadUI.$el[0],
            () => {
                if (callback) {
                    callback();
                }
            },
        );
    },
};

/* HACK -- This is for displaying the scratchpads in tutorials. We put this
at the global scope so that we can use JQuery off and on to ensure that
we unbind all existing listeners before binding our listener. This can
go away once everything is in Backbone and nice. */
function scratchpadListenKeydown(e: KeyboardEvent) {
    /**
     * TODO(joel): Figure out how to make this work in IE9 without
     * disabling backspace in the discussion inputs.
     * http://phabricator.khanacademy.org/T248
     */
    // Prevent backspace from navigating the browser
    // But not when we're inside an input or a contenteditable area
    if (
        !$.browser.msie &&
        e.which === 8 &&
        !$(e.target).is(":input") &&
        $(e.target).closest("[contenteditable=true]").length === 0
    ) {
        e.preventDefault();
    }

    // Make Cmd-S/Ctrl-S save
    if (e.which === 83 && (e.ctrlKey || e.metaKey)) {
        if (ScratchpadUI.isSaveable()) {
            ScratchpadUI.triggerSave(false);
        }
        e.preventDefault();
    }
}
ScratchpadUI.initAndBind = function (this: any) {
    if (maybeRedirectToTutorialView()) {
        return;
    }

    $(ScratchpadUI).trigger("load");

    /*
     * Bind events.
     */

    this.$el.on("click", ".fork", function (e) {
        if ($(e.target).hasClass("discussion-author")) {
            $(ScratchpadUI).trigger("clickedSpinoffAuthor");
        } else {
            $(ScratchpadUI).trigger("clickedSpinoff");
        }
    });

    this.$el.on("click", ".hover-card-content a", function () {
        $(ScratchpadUI).trigger("clickedSpinoffAuthor");
    });

    // Make sure we're not double binding this.
    $(window).unbind("beforeunload.scratchpad");

    // Watch for when the user is leaving the page.
    // Used for temporarily saving a user's code when they're making a new
    // scratchpad.
    $(window).bind("beforeunload.scratchpad", function () {
        ScratchpadUI.triggerRemove();

        // Only warn if we're not on a new scratchpad
        if (!ScratchpadUI.scratchpad.isNew()) {
            const options = {
                defaultMsg: t`You have unsaved changes! Are you sure you wish to leave the page?`,
            } as const;

            // Only prompt if the user is able to save and the code has changed.
            if (ScratchpadUI.hasUnsavedChanges()) {
                // @ts-expect-error - TS2339 - Property 'warnMsg' does not exist on type '{ readonly defaultMsg: string; }'.
                options.warnMsg = options.defaultMsg;
            }

            $(ScratchpadUI).trigger("beforeunload", options);
            // @ts-expect-error - TS2339 - Property 'warnMsg' does not exist on type '{ readonly defaultMsg: string; }'.
            return options.warnMsg;
        }
    });
};

/*
 * True if the user is attempting a challenge/project,
 * false if its a developer trying to edit it.
 */
ScratchpadUI.isAttemptingSomething = function () {
    // If the challenge/project is embedded, then it's never being edited
    // and always being attempted, but only if we're not in edit mode
    if (ScratchpadUI.embedded && !ScratchpadUI.inEditMode()) {
        return true;
    }
    return !ScratchpadUI.canEditCurrentScratchpad();
};

/**
 * Used by projects and challenges
 * @param options Options to pass to the UserScratchpad save or falsy for none
 * @param currentCode The code to stash, or undefined for the current code
 */
ScratchpadUI.stashUserCode = function (options: unknown, currentCode: unknown) {
    if (!ScratchpadUI.liveEditor) {
        return;
    }
    if (_.isUndefined(currentCode)) {
        currentCode = ScratchpadUI.liveEditor.editor.text();
    }

    options = options || {};
    // Only send the changed stashedCode attribute,
    // so that we don't override values saved elsewhere
    const attrs = _.extend(
        {
            stashedCode: currentCode,
        },
        // @ts-expect-error - TS2571 - Object is of type 'unknown'.
        options.attrs || {},
    );
    // @ts-expect-error - TS2571 - Object is of type 'unknown'.
    options.attrs = attrs;

    if (ScratchpadUI.scratchpad.isChallenge() && ScratchpadUI.challenge) {
        ScratchpadUI.challenge
            .updateChallengeProgress(attrs.stashedCode, attrs.challengeStatus)
            .then(() => {
                // @ts-expect-error - TS2571 - Object is of type 'unknown'.
                options.success?.();
                // ScratchpadUI.stashedCode is also used to store the stashed
                // code after the initCode event is fired.  Save it here too so
                // we have a common place to compare from in
                // ScratchpadUI.hasUnsavedChanges.
                ScratchpadUI.stashedCode = currentCode;
            })
            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            .catch(() => options.error?.());
        return;
    }

    if (ScratchpadUI.scratchpad.isProject()) {
        ScratchpadUI.updateProjectProgress(
            attrs.stashedCode,
            attrs.challengeStatus,
        )
            .then(() => {
                // @ts-expect-error - TS2571 - Object is of type 'unknown'.
                options.success?.();
                // ScratchpadUI.stashedCode is also used to store the stashed code after
                // the initCode event is fired.  Save it here too so we have a common
                // place to compare from in ScratchpadUI.hasUnsavedChanges.
                ScratchpadUI.stashedCode = currentCode;
            })
            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            .catch(() => options.error?.());
    }
};

/*
 * If the scratchpad has a tutorial_url and we aren't potentially editing it
 * we should redirect the page.
 * We use the no_redirect query parameter if the user is viewing a redirected
 * project fork.  In this case, going to the scratchpad URI would just
 * redirect the user back to their fork.
 */
const maybeRedirectToTutorialView = function () {
    const params = new URLSearchParams(
        // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
        // eslint-disable-next-line no-restricted-syntax
        window.location.search,
    );
    const tutorialUrl = ScratchpadUI.scratchpad.get("defaultUrlPath");
    if (
        tutorialUrl &&
        !ScratchpadUI.embedded &&
        !ScratchpadUI.canEditCurrentScratchpad() &&
        !params.get("no_redirect")
    ) {
        // Copy query parameters over, so that we retain the QA expand key
        // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
        // eslint-disable-next-line no-restricted-syntax
        ScratchpadUI.forceRedirect(tutorialUrl + window.location.search);
        return true;
    }

    return false;
};

/*
 * Load a Scratchpad into the site and make it editable
 */
ScratchpadUI.startScratch = function (
    queryParams: URLSearchParams,
    options: unknown,
) {
    const start = () => {
        ScratchpadUI.initLiveEditor(queryParams, options);
        ScratchpadUI.initAndBind();
        ScratchpadUI.initKeybindings();

        const codeOptions = {code: ""} as const;
        $(ScratchpadUI).trigger("initCode", codeOptions);
        ScratchpadUI.stashedCode = codeOptions.code;
        if (ScratchpadUI.stashedCode) {
            ScratchpadUI.liveEditor.editor.text(ScratchpadUI.stashedCode);
        }

        $(ScratchpadUI).trigger("started");
    };

    ScratchpadUI.renderScratchpad(queryParams, options, () => {
        start();
    });
}; // end startScratch

/**
 * Returns a boolean determining if the user's code has some non-deterministic
 * code running (and thus should have the restart button shown).
 */
const shouldHideRestart = function () {
    const currentCode = ScratchpadUI.liveEditor.editor.text();
    const contentType = ScratchpadUI.scratchpad.get("userAuthoredContentType");

    if (contentType === "webpage") {
        if (currentCode.match(/<script>/) || currentCode.match(/animation:/)) {
            return false;
        }
    } else if (contentType === "pjs") {
        const checks = [
            "function(){var draw = function(){};}",
            "function(){draw = function(){};}",
            "function(){var mousePressed = function(){};}",
            "function(){var mouseReleased = function(){};}",
            "function(){var mouseMoved = function(){};}",
            "function(){var mouseClicked = function(){};}",
            "function(){var keyPressed = function(){};}",
            "function(){mousePressed = function(){};}",
            "function(){mouseReleased = function(){};}",
            "function(){mouseMoved = function(){};}",
            "function(){mouseClicked = function(){};}",
            "function(){keyPressed = function(){};}",
            "function(){random();}",
            "function(){var _ = new Random();}",
            "function(){var _ = random();}",
        ];

        try {
            // Don't worry, StructuredJS caches the parsing of the code and
            // the structure tests!
            for (let i = 0; i < checks.length; i++) {
                if (ScratchpadUI.Structured.match(currentCode, checks[i])) {
                    return false;
                }
            }
            return true;
        } catch (e: any) {
            // Don't have to do anything on syntax error, just ignore it
            // and leave the restart button in its current state.
        }
    }

    return true;
};

// @ts-expect-error - TS2339 - Property 'ScratchpadUI' does not exist on type 'Window & typeof globalThis'.
window.ScratchpadUI = ScratchpadUI;
// Allow other (old) code to do a legacy "ifLoaded" call on this module.
LegacyRequireIfLoaded.ScratchpadUIModule = {
    ScratchpadUI: ScratchpadUI,
};
